package floris.graphics.backoffice.pages.admin.categories

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.ui.modifiers.textOverflow
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import floris.graphics.backoffice.components.layouts.PageLayout
import floris.graphics.core.navigation.Navigation
import com.materialdesignsystem.components.widgets.DsCard
import com.materialdesignsystem.components.widgets.DsEmptyScreen
import com.materialdesignsystem.components.widgets.DsSpinner
import com.materialdesignsystem.components.widgets.SpinnerSize
import com.materialdesignsystem.toColorScheme
import floris.graphics.models.domain.models.Category
import kotlinx.coroutines.flow.collectLatest
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.A

@Page("/admin/categories")
@Composable
fun CategoriesPage() {
    val breakpoint = rememberBreakpoint()
    val context = rememberPageContext()

    val viewModel = remember { CategoriesViewModel() }
    val state by viewModel.state.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.eventFlow.collectLatest {
            when (it) {
                is CategoriesUiEvent.Navigate -> context.router.navigateTo(it.route)
            }
        }
    }

    PageLayout(
        title = "Categories",
        fabOnClick = { viewModel.onEvent(CategoriesEvent.Navigate(Navigation.Screen.Admin.Categories.Manage().route)) }
    ) {
        if (state.isLoading) {
            Box(
                modifier = Modifier.fillMaxSize(),
                contentAlignment = Alignment.Center
            ) {
                DsSpinner(size = SpinnerSize.Large)
            }

            return@PageLayout
        }

        if (!state.isLoading && state.categories.isEmpty()) {
            DsEmptyScreen(
                title = "Geen categorieën gevonden",
                description = "Maak jou eerste categorie aan om deze daarna hier terug te vinden!",
                actionButtonText = "Voeg een nieuwe categorie toe",
                onActionButtonClick = { viewModel.onEvent(CategoriesEvent.Navigate(Navigation.Screen.Admin.Categories.Manage().route)) }
            )

            return@PageLayout
        }

        SimpleGrid(
            modifier = Modifier
                .fillMaxWidth(if (breakpoint > Breakpoint.MD) 80.percent else 90.percent)
                .padding(top = if (breakpoint >= Breakpoint.MD) 58.px else 36.px)
                .gap(
                    rowGap = if (breakpoint > Breakpoint.MD) 36.px else 24.px,
                    columnGap = if (breakpoint > Breakpoint.MD) 36.px else 24.px
                ),
            numColumns = numColumns(base = 1, sm = 2, xl = 3)
        ) {
            state.categories.forEach { category ->
                CategoryCard(
                    category = category
                )
            }
        }
    }
}

@Composable
fun CategoryCard(
    modifier: Modifier = Modifier,
    category: Category
) {
    val breakpoint = rememberBreakpoint()
    val colorScheme = ColorMode.current.toColorScheme

    A(
        href = Navigation.Screen.Admin.Categories.Manage(category.id).route,
        attrs = modifier
            .textDecorationLine(TextDecorationLine.None)
            .toAttrs()
    ) {
        DsCard(
            contentPadding = if (breakpoint >= Breakpoint.MD) 30.px else 16.px,
            content = {
                SpanText(
                    modifier = Modifier
                        .color(colorScheme.onBackground)
                        .fontSize(20.px)
                        .fontWeight(FontWeight.Bold)
                        .textOverflow(TextOverflow.Ellipsis),
                    text = category.name
                )
            }
        )
    }
}
